import React, { Fragment } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useFilters,
  useExpanded,
  usePagination,
  useRowSelect,
} from "react-table";
import { Table, Row, Col, CardBody } from "reactstrap";
import { DefaultColumnFilter } from "./filters";
import { ContactsGlobalFilter } from "../../Components/Common/GlobalSearchFilter";
import { Link } from "react-router-dom";

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  isUsersFilter,
  handleUserClick,
  HeaderTable,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <React.Fragment>
      <CardBody className="border border-dashed border-end-0 border-start-0">
        <form>
          <Row className="g-3">
            <Col lg={6} className="search-box me-2 mb-2">
              <input
                onChange={(e) => {
                  setValue(e.target.value);
                  onChange(e.target.value);
                }}
                id="search-bar-0"
                type="text"
                className="form-control search /"
                placeholder={`Rechercher sur ${count} éléments ...`}
                value={value || ""}
              />
              <i className="bx bx-search-alt search-icon"></i>
            </Col>
            {isUsersFilter && (
              <Col lg={2}>
                <ContactsGlobalFilter />
              </Col>
            )}
            <Col>{/* <HeaderTable/> */}</Col>
          </Row>
        </form>
      </CardBody>
    </React.Fragment>
  );
}

const TableContainerMin = ({
  columns,
  data,
  isGlobalSearch,
  isGlobalFilter,
  isUsersFilter,
  handleUserClick,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  HeaderTable,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: 0,
        pageSize: customPageSize,
        selectedRowIds: 0,
        sortBy: [
          {
            desc: true,
          },
        ],
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const generateSortingIndicator = (column) => {
    return column.isSorted ? (column.isSortedDesc ? " " : "") : "";
  };

  const onChangeInSelect = (event) => {
    setPageSize(Number(event.target.value));
  };
  const onChangeInInput = (event) => {
    const page = event.target.value ? Number(event.target.value) - 1 : 0;
    gotoPage(page);
  };

  const getPaginationGroup = () => {
    const totalPages = pageOptions.length;
    const currentPage = pageIndex + 1;
    const delta = 1;

    const pages = [];

    pages.push(1);
    
    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      pages.push(i);
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    const uniquePages = [...new Set(pages)];

    const finalPages = [];

    for (let i = 0; i < uniquePages.length; i++) {
      if (i > 0 && uniquePages[i] - uniquePages[i - 1] > 1) {
        finalPages.push("...");
      }

      finalPages.push(uniquePages[i]);
    }

    return finalPages;
  };

  const paginationGroup = getPaginationGroup();

  return (
    <Fragment>
      <Row className="mb-3">
        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
            isUsersFilter={isUsersFilter}
            handleUserClick={handleUserClick}
            HeaderTable={HeaderTable}
          />
        )}
      </Row>

      <div className={divClass}>
        <Table hover {...getTableProps()} className={tableClass}>
          <thead className={theadClass}>
            {headerGroups.map((headerGroup) => (
              <tr
                className={trClass}
                key={headerGroup.id}
                {...headerGroup.getHeaderGroupProps()}
              >
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    className={thClass}
                    {...column.getSortByToggleProps()}
                  >
                    {column.render("Header")}
                    {generateSortingIndicator(column)}
                    {/* <Filter column={column} /> */}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <Fragment key={row.getRowProps().key}>
                  <tr>
                    {row.cells.map((cell) => {
                      return (
                        <td key={cell.id} {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>

      <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
        <div className="col-sm">
          <div className="text-muted">
            Affichage<span className="fw-semibold ms-1">{page.length}</span> sur{" "}
            <span className="fw-semibold">{data.length}</span> Résultats
          </div>
        </div>
        {isGlobalSearch && (
          <Col md={3}>
            <select
              className="form-select"
              value={pageSize}
              onChange={onChangeInSelect}
            >
              {[10, 20, 30, 40, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Affichage par {pageSize} lignes
                </option>
              ))}
            </select>
          </Col>
        )}

        <div className="col-sm-auto">
          <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
            <li
              className={!canPreviousPage ? "page-item disabled" : "page-item"}
            >
              <Link to="#" className="page-link" onClick={previousPage}>
                Précédent
              </Link>
            </li>
            {paginationGroup.map((item, index) => {
              if (item === "...") {
                return (
                  <li key={index} className="page-item disabled">
                    <span className="page-link">...</span>
                  </li>
                );
              }

              return (
                <li key={index} className="page-item">
                  <Link
                    to="#"
                    className={
                      pageIndex + 1 === item
                        ? "page-link active"
                        : "page-link"
                    }
                    onClick={() => gotoPage(item - 1)}
                  >
                    {item}
                  </Link>
                </li>
              );
            })}
            <li className={!canNextPage ? "page-item disabled" : "page-item"}>
              <Link to="#" className="page-link" onClick={nextPage}>
                Suivant
              </Link>
            </li>
          </ul>
        </div>
      </Row>
    </Fragment>
  );
};

TableContainerMin.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default TableContainerMin;
